import { useState, useEffect, useCallback, useRef } from 'react';
import { horodatyApi } from '@/lib/utils/horodaty-api';

/**
 * Hook pour récupérer un rapport de procédure (intervention de type procedure)
 *
 * Ce hook gère automatiquement la récupération des données complètes en deux étapes :
 * 1. Récupère l'intervention minimale via /company-manager/interventions/{id} pour obtenir le folder_uniq_id
 * 2. Récupère les données complètes (avec procedureReport) via /folders/{folder}/interventions/{id}
 *
 * @param {string} interventionId - ID de l'intervention (rapport)
 *
 * @returns {{
 *   report: {
 *     uniq_id: string,
 *     name: string,
 *     ref: string,
 *     type: 'procedure',
 *     procedureReport: {
 *       template_id: number,
 *       template_version_id: number,
 *       answers: object
 *     },
 *     procedureTemplateVersion: {
 *       configuration: object
 *     },
 *     documents: Array<...>
 *   } | null,
 *   isLoading: boolean,
 *   error: Error | null,
 *   notFound: boolean,
 *   refetch: () => Promise<void>
 * }}
 */
export function useProcedureReport(procedureId, interventionId) {
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const isMountedRef = useRef(true);

    const fetchReport = useCallback(async () => {
        if (!interventionId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setNotFound(false);

        try {
            // Étape 1: Récupérer l'intervention minimale pour obtenir le folder_uniq_id
            const minimalResponse = await horodatyApi.procedureTemplates.getReport(procedureId, interventionId);
            console.log(minimalResponse.data);
            console.log(minimalResponse.data?.report);
            
            const minimalData = minimalResponse.data?.report || minimalResponse.data;

            if (!minimalData) {
                if (isMountedRef.current) {
                    setNotFound(true);
                    setIsLoading(false);
                }
                return;
            }

            const folderId = minimalData.folder_uniq_id;

            if (!folderId) {
                if (isMountedRef.current) {
                    setError(new Error('Impossible de trouver le dossier associé à cette intervention'));
                    setIsLoading(false);
                }
                return;
            }

            // Étape 2: Récupérer les données complètes avec procedureReport
            let fullData = null;
            try {
                const fullResponse = await horodatyApi.getIntervention(folderId, interventionId);
                fullData = fullResponse.data?.data || fullResponse.data;
            } catch (fullErr) {
                // Si l'appel complet échoue (ex: erreur certificates sur l'API), utiliser les données minimales
                console.warn('Fallback to minimal data:', fullErr?.response?.data?.message || fullErr.message);
                fullData = minimalData;
            }

            if (isMountedRef.current) {
                // Vérifier que c'est bien une procédure (tolérant si procedureReport absent)
                if (fullData.type !== 'procedure' && fullData.type !== 'PROCEDURE' && !fullData.procedureReport) {
                    setNotFound(true);
                    setError(new Error("Cette intervention n'est pas une procédure"));
                } else {
                    // read_only vient de la route procédure (rapport d'une entreprise fille)
                    setReport({ ...fullData, read_only: minimalData.read_only === true });
                }
                setIsLoading(false);
            }
        } catch (err) {
            if (isMountedRef.current) {
                if (err.response?.status === 404) {
                    setNotFound(true);
                } else {
                    setError(err);
                }
                setIsLoading(false);
            }
        }
    }, [interventionId]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchReport();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchReport]);

    return {
        report,
        isLoading,
        error,
        notFound,
        refetch: fetchReport,
    };
}

/**
 * Extrait les réponses d'un rapport sous forme de dictionnaire { fieldId: value }
 *
 * @param {object} report - Le rapport de procédure
 * @returns {object} - { [fieldId]: value }
 */
export function extractFormDataFromReport(report) {
    const formData = {};

    const answers = report?.procedureReport?.answers;
    if (!answers?.sections) {
        return formData;
    }

    for (const section of answers.sections) {
        for (const field of section.fields || []) {
            formData[field.id] = field.value;
        }
    }

    return formData;
}

/**
 * Récupère la configuration du template utilisée par le rapport
 *
 * @param {object} report - Le rapport de procédure
 * @returns {object | null} - La configuration { sections: [...] }
 */
export function getReportConfiguration(report) {
    return report?.procedureReport?.procedureTemplateVersion?.configuration || null;
}

/**
 * Vérifie si le rapport utilise une ancienne version du template
 *
 * @param {object} report - Le rapport de procédure
 * @param {object} template - Le template actuel
 * @returns {{ isOutdated: boolean, reportVersion: number, currentVersion: number }}
 */
export function checkReportVersionStatus(report, template) {
    const reportVersionId = report?.procedureReport?.template_version_id;
    const currentVersion = template?.current_version;

    if (!reportVersionId || !currentVersion) {
        return { isOutdated: false, reportVersion: null, currentVersion: null };
    }

    return {
        isOutdated: reportVersionId < currentVersion,
        reportVersion: reportVersionId,
        currentVersion,
    };
}
